// src/services/siteRoute.js — the three public URLs, and nothing more.
//
// This client has never had a router. Everything inside the app is a `view`
// held in state, which is right for a chat client — a conversation is not a
// document you deep-link to, and a router would have added a dependency to
// solve a problem nobody had.
//
// A marketing page IS a document you deep-link to. Somebody pastes
// allloapp.com/about into a message; a search engine indexes it; the back
// button has to work. So there are now exactly three site routes, resolved from
// the path by hand:
//
//     /        the Home page
//     /about   the About page
//     /app     the actual product (which then does its own thing internally)
//
// 🟥 /g/<token> IS NOT IN THIS FILE AND MUST NEVER BE. The guest route is
//    decided in App.jsx ABOVE everything else, before this module is even
//    imported, because a guest URL carries the conversation's password and
//    nothing that reads or reports a path may run on it. Adding a 'guest' case
//    here would create a second place that has to get it right — and the whole
//    design of the guest guard is that there is one place, as early as possible.

export const HOME = 'home';
export const ABOUT = 'about';
export const APP = 'app';

/** Path → route. Anything unrecognised is Home, which is what a stray link wants. */
export function routeFromPath(pathname) {
  const p = String(pathname == null ? window.location.pathname : pathname)
    .toLowerCase()
    /* 🟢 one trailing slash, gone. firebase.json sets trailingSlash:false, so
       Hosting will not redirect /about/ for us — it serves index.html and hands
       us the path verbatim. Without this, /about/ silently renders Home. */
    .replace(/\/+$/, '');
  if (p === '/about') return ABOUT;
  if (p === '/app' || p.startsWith('/app/')) return APP;
  if (p === '' || p === '/') return HOME;
  /* 🟢 An unknown path is Home rather than a 404 page. Hosting's `**` rewrite
     already served index.html for it, so a 404 here would be a lie the URL bar
     contradicts — and the paths that actually matter (/approve, /app/vibes)
     are analytics-only virtuals that were never real URLs to begin with. */
  return HOME;
}

/** Route → the path it should show. */
export function pathForRoute(route) {
  if (route === ABOUT) return '/about';
  if (route === APP) return '/app';
  return '/';
}

/**
 * Move to a route and put it in the browser's history, so Back works.
 * Returns the route so a caller can `setRoute(navigate(ABOUT))`.
 */
export function navigate(route, { replace = false } = {}) {
  const path = pathForRoute(route);
  if (window.location.pathname !== path) {
    window.history[replace ? 'replaceState' : 'pushState']({ route }, '', path);
  }
  /* 🟢 pushState does NOT fire popstate — that event is for the user pressing
     Back, not for us calling the API. Anything listening for route changes
     (the App shell, the analytics pageview) would sit silent on every in-page
     navigation, so we say so ourselves. */
  window.dispatchEvent(new Event('allo:route'));
  return route;
}

/** Subscribe to route changes — both ours and the Back button's. */
export function onRouteChange(fn) {
  const cb = () => fn(routeFromPath());
  window.addEventListener('popstate', cb);
  window.addEventListener('allo:route', cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener('allo:route', cb);
  };
}

/** Human titles, for the document title and the analytics pageview. */
export const ROUTE_TITLES = {
  [HOME]: 'Allo — private messaging for phone and web',
  [ABOUT]: 'About — Allo',
  [APP]: 'Allo for Web',
};
